import { Menu, MenuItem, SwipeableDrawer, ListItemText, Toolbar } from "@mui/material";
import { styled } from "@mui/material/styles";
import { makeStyles } from "@mui/styles";
import { colors } from "@zapalacorp/tokens";

interface ItemInfoWrapperProps {
  disabled?: boolean;
  isSwipeable?: boolean;
}

export const StyledMenu = styled(Menu)(() => ({
  "& .MuiPaper-root": {
    fontFamily: "Manrope !important",
    fontSize: 18,
    borderRadius: "12px",
    boxShadow: "0px 4px 10px rgba(15, 56, 104, 0.25)",
    minWidth: "180px",
    "& .MuiMenu-list": {
      fontSize: 18,
    },
    "& .MuiMenuItem-root": {
      padding: "8px 16px",
      font: "normal 400 12px/24px Manrope",
      "& svg": {
        fontSize: 18,
        marginRight: 25,
      },
    },
    "& .MuiMenuItem-root.Mui-selected": {
      backgroundColor: "#EDF5FB",
      color: "#308CDB",
      fontWeight: 600,
    },
    "& .MuiMenuItem-root:hover": {
      backgroundColor: "#EDF5FB",
      color: "#308CDB",
    },
  },
}));

export const UserMenuIcon = styled("div")(() => ({
  width: "40px",
  height: "40px",
  borderRadius: "50%",
  position: "relative",
  cursor: "pointer",
}));

export const UserMenuIconImage = styled("img")(() => ({
  width: "40px",
  height: "40px",
  borderRadius: "50%",
  position: "absolute",
  transform: "translate(-50%, -50%)",
  top: "50%",
  left: "50%",
  bottom: 0,
  right: 0,
  objectFit: "cover",
}));

export const UserMenuItem = styled(MenuItem)(() => ({
  display: "flex",
  gap: "16px",

  padding: "8px 24px 8px 16px",
  width: "100%",
  pointerEvents: "none",
}));

export const UserAvatarContainer = styled("div")({
  alignSelf: "flex-start",
  paddingTop: "4px",
});

export const MenuIconWrapper = styled("div")(() => ({
  alignItems: "center",
  display: "flex",
  justifyContent: "center",

  height: "fit-content",
  padding: "8px",

  cursor: "pointer",

  "@media screen and (min-width: 768px)": {
    borderRadius: 8,

    "&:hover": {
      backgroundColor: "#6670851F",
      color: colors.gray600,
    },
  },
}));

export const ItemInfoWrapper = styled("div", {
  shouldForwardProp: (props) => props !== "isSwipeable",
})(({ disabled = false, isSwipeable = false }: ItemInfoWrapperProps) => ({
  alignCenter: "center",
  display: "flex",
  gap: "12px",

  color: "#475467",
  fontFamily: "Manrope !important",
  fontSize: "14px !important",
  fontStyle: "normal !important",
  fontHeight: "400 !important",
  lineWeight: "20px !important",
  letterSpacing: "0.25px !important",
  padding: isSwipeable ? "16px 12px" : "16px",
  width: "100%",

  "& svg": {
    marginRight: "0px !important",
  },

  cursor: disabled ? "default" : "pointer",
  opacity: disabled ? "0.38" : "1",

  ":hover": {
    background: disabled ? "transparent" : "#EDF5FB",
  },
}));

export const ItemInfoWrapperJustified = styled(ItemInfoWrapper)(() => ({
  justifyContent: "space-between",
}));

export const UserInfos = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  gap: "0px",
  justifyContent: "flex-start",
  alignItems: "flex-start",
  rowGap: "4px",
}));

export const DrawerUserInfos = styled(UserInfos)(() => ({
  gap: "8px",
}));

export const DrawerUserInfosContainer = styled(DrawerUserInfos)(() => ({
  padding: "8px 16px",
}));

export const Divider = styled("hr")(() => ({
  height: "1px",
  margin: "0px",
  width: "100%",
}));

export const DrawerTitle = styled("h5")(() => ({
  color: "#0055A4",
  fontFamily: "Manrope",
  fontSize: "24px",
  fontStyle: "normal",
  fontWeight: 700,
  lineHeight: "32px",
}));

export const StyledSwipeableDrawer = styled(SwipeableDrawer)(() => ({
  "& .MuiDrawer-paper": {
    maxWidth: "280px !important",
    width: "100% !important",
  },

  "& .MuiList-padding": {
    padding: "0",
  },
}));

export const Subtitle = styled("h6")(() => ({
  color: "#0055A4",
  fontFamily: "Manrope",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 700,
  lineHeight: "20px",
  letterSpacing: "0.1px",
  margin: 0,
}));

export const PrimaryText = styled("span")(() => ({
  color: "#475467",
  fontFamily: "Manrope !important",
  fontSize: "14px !important",
  fontStyle: "normal !important",
  fontWeight: 700,
  lineHeight: "20px !important",
  letterSpacing: "0.25px !important",
}));

export const IconAndSecondaryText = styled("div")({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  "& svg": {
    marginRight: "4px !important",
  },
});

export const SecondaryText = styled("span")(() => ({
  color: colors.gray600,
  fontFamily: "Manrope !important",
  fontSize: "12px !important",
  fontStyle: "normal !important",
  fontWeight: 400,
  lineHeight: "20px !important",
  letterSpacing: "0.25px !important",
}));

export const ThirdText = styled("span")(() => ({
  color: "#667085",
  fontFamily: "Manrope",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "20px",
  letterSpacing: "0.25px",
}));

export const LogOutText = styled(ListItemText)(() => ({
  "& span": {
    color: "#D92D20",
    fontFamily: "Manrope",
    fontSize: "14px",
    fontWeight: 400,
    lineHeight: "20px",
    letterSpacing: "0.25px",
  },
}));

export const StyledToolbar = styled(Toolbar)(() => ({
  padding: "0 !important",
}));

const useStyles = makeStyles(() => ({
  swipeArea: {
    "& MuiDrawer-paper": {
      height: "0px !important",
      maxWidth: "280px !important",
      width: "100% !important",
    },
  },
  link: {
    marginBottom: "5px",
  },
  customBadge: {
    backgroundColor: "#38CFC4",
    marginTop: "3px",
    padding: 0,
    height: "16px",
    color: "#FFFFFF",
    fontFamily: "Poppins",
    fontSize: "10px",
    fontWeight: 700,
    transform: "scale(1) translate(0%, 50%)",
  },
  companyProfileName: {
    padding: "0 10px",
  },
  image: {
    height: "35px",
    marginBottom: "5px",
  },
  containerTitleSelected: {
    padding: "3px 0px",
    boxShadow: " 0px 3px 0px #38CFC4",
    cursor: "pointer",
    marginLeft: "32px",
  },
  containerTitle: {
    cursor: "pointer",
    "&:hover span": {
      color: "#38CFC4 !important",
    },
    marginLeft: "32px",
  },
  title: {
    fontFamily: "Manrope",
    fontSize: "12px",
    fontWeight: 600,
    lineHeight: "24px",
    transition: "all .2s ease-in-out",
  },
  titleSelected: {
    color: "#38CFC4",
  },
  titleGray: {
    color: "#93A9BD",
  },
  containerNavBarLink: {
    display: "flex",
    alignItems: "center",
  },
  paperProps: {
    display: "flex",
    alignItems: "flex-start",
    flexDirection: "column",

    marginTop: "27px",
    padding: "0px",
    width: "320px",

    "& ul": {
      padding: "0",
      width: "100%",
    },
  },
  menuItem: {
    alignCenter: "center",
    display: "flex",
    gap: "12px",

    color: colors.gray600,
    cursor: "pointer",
    fontFamily: "Manrope !important",
    fontSize: "14px !important",
    fontStyle: "normal !important",
    fontHeight: "400 !important",
    lineWeight: "20px !important",
    letterSpacing: "0.25px !important",
    padding: "16px !important",
    width: "100%",

    "& svg": {
      marginRight: "0px !important",
    },

    "&:hover": {
      color: `${colors.gray600} !important`,
    },
  },
  menuItemSwipeable: {
    padding: "16px 12px !important",
  },
  logOutItem: {
    alignCenter: "center",
    display: "flex",
    gap: "12px",
    padding: "16px 12px",

    color: "#D92D20",
    fontFamily: "Manrope !important",
    fontSize: "14px !important",
    fontStyle: "normal !important",
    fontWeight: 400,
    lineHeight: "20px !important",
    letterSpacing: "0.25px !important",
  },
  logOutItemSwipeable: {
    padding: "16px",
  },
  centerAlign: {
    alignItems: "center",
    display: "flex",
    gap: "12px",
  },
  justifyContentSpaceBetween: {
    justifyContent: "space-between",
  },
  button: {
    backgroundColor: "transparent !important",
    height: "32px",
    padding: "5px",
    alignItems: "center",
    transition: "none",
    marginRight: "16px",
    borderRadius: "15px",
    cursor: "pointer",
    "& span": {
      display: "flex",
      gap: "10px",
    },
  },
  buttonActive: {
    backgroundColor: "#DAEAF8 !important",
    height: "32px",
    alignItems: "center",
    padding: "5px",
    transition: "none",
    marginRight: "16px",
    borderRadius: "15px",
    cursor: "pointer",
    "& span": {
      display: "flex",
      gap: "10px",
    },
  },
  menuItemDisabled: {
    cursor: "default",
    opacity: "0.38",

    ":hover": {
      background: "transparent",
      color: "#475467",
    },
  },
  drawerHeaderContainer: {
    alignItems: "flex-start",
    display: "flex",
    flexDirection: "column",
  },
  drawerHeader: {
    alignItems: "flex-start",
    display: "flex",
    flexDirection: "column",
    gap: "8px",

    padding: "20px 16px",
  },
}));

export default useStyles;
